import React, { useState, useEffect } from 'react';
import { colors } from '@styles/colors';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CloseIcon from '@mui/icons-material/Close';
import Resumen from '@components/Resumen';
import { LAYOUT_COMPACT_MAX_PX } from '@utils/breakpoints';

// Componente reutilizable para títulos
const Titulo = ({
  children: _children,
  color = colors.accent,
  titulo = '',
  subtitulo = '',
  rutaRetorno = null,
  stateRetorno = null,
  imagen_titulo = 'doc',
  /** Props del Resumen de agendamiento (móvil/tablet: botón + overlay). En escritorio no afecta. */
  resumenAgendaProps = null,
}) => {
  const navigate = useNavigate();
  const [isCompact, setIsCompact] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= LAYOUT_COMPACT_MAX_PX
  );
  const [resumenOverlayAbierto, setResumenOverlayAbierto] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${LAYOUT_COMPACT_MAX_PX}px)`);
    const apply = () => setIsCompact(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (!resumenOverlayAbierto) {
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [resumenOverlayAbierto]);

  const tieneResumen = resumenAgendaProps != null && typeof resumenAgendaProps === 'object';
  const mostrarBotonResumenMovil = isCompact && tieneResumen;

  // Función para obtener la ruta de la imagen según el tipo
  const obtenerRutaImagen = (tipo) => {
    switch (tipo) {
      case 'doc':
        return '/doc.png';
      case 'fecha':
        return '/calendar.png';
      case 'ubica':
        return '/ubica.png';
      case 'medi':
        return '/medi.png';
      case 'pagar':
        return '/pagar.png';
      case 'hora':
        return '/hora.png';
      default:
        return '/doc.png';
    }
  };

  return (
    <>
      {mostrarBotonResumenMovil && resumenOverlayAbierto && (
        <div
          role="presentation"
          onClick={() => setResumenOverlayAbierto(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1200,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2vh',
            boxSizing: 'border-box',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Resumen de agendamiento"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
            }}
          >
            <button
              type="button"
              onClick={() => setResumenOverlayAbierto(false)}
              aria-label="Cerrar resumen"
              style={{
                position: 'absolute',
                top: '-0.5vh',
                right: '-0.5vh',
                zIndex: 2,
                width: '4.2vh',
                height: '4.2vh',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: colors.white,
                boxShadow: '0 0.2vh 0.8vh rgba(0,0,0,0.2)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <CloseIcon style={{ fontSize: '2.2vh', color: colors.gray700 }} />
            </button>
            <Resumen {...resumenAgendaProps} variant="overlay" />
          </div>
        </div>
      )}

      <div style={{
        width: '100%',
        maxWidth: '100%',
        alignSelf: 'stretch',
        overflow: 'visible',
        boxSizing: 'border-box',
      }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: isCompact ? 'flex-end' : 'flex-start',
            alignItems: 'center',
            width: '100%',
            minHeight: '4.6vh',
            paddingRight: isCompact ? '2vh' : 0,
            boxSizing: 'border-box',
            gap: isCompact ? '1.2vh' : 0,
            position: 'relative',
            zIndex: 5,
          }}
        >
          <div
            id="cajaIconoRetorno"
            onClick={() => {
              if (rutaRetorno) {
                if (stateRetorno) {
                  navigate(rutaRetorno, { state: stateRetorno });
                } else {
                  navigate(rutaRetorno);
                }
              }
            }}
            style={{
              width: '4.6vh',
              height: '4.6vh',
              backgroundColor: color,
              borderRadius: '3vh',
              transform: 'rotate(0deg)',
              opacity: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.6vh',
              fontFamily: 'Inter',
              fontWeight: 'bold',
              color: '#fff',
              marginLeft: isCompact ? '0' : '6vh',
              marginTop: isCompact ? '2vh' : '0',
              cursor: rutaRetorno ? 'pointer' : 'default',
              flexShrink: 0,
              order: isCompact ? 2 : 0,
            }}
          >
            <ArrowBackIcon style={{ width: '4vh', height: '3vh', color: '#fff' }} />
          </div>
          {mostrarBotonResumenMovil && (
            <button
              type="button"
              id="cajaIconoResumenAgenda"
              aria-label="Ver resumen de agendamiento"
              onClick={() => setResumenOverlayAbierto(true)}
              style={{
                width: '4.6vh',
                height: '4.6vh',
                minWidth: '44px',
                minHeight: '44px',
                backgroundColor: color,
                borderRadius: '3vh',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: isCompact ? '2vh' : '0',
                padding: 0,
                cursor: 'pointer',
                flexShrink: 0,
                order: isCompact ? 1 : 0,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <ListAltIcon style={{ width: '2.6vh', height: '2.6vh', color: '#fff' }} />
            </button>
          )}
        </div>
        <div id="cajaTitulo" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          borderRadius: isCompact ? '2vh' : '8.76vh',
          width: isCompact ? 'calc(100% - 4vh)' : '90vh',
          height: isCompact ? 'auto' : '12vh',
          minHeight: '12vh',
          marginTop: '2vh',
          marginLeft: isCompact ? '2vh' : '6vh',
          marginRight: isCompact ? '2vh' : '0',
          backgroundColor: '#ACF1F9',
          boxSizing: 'border-box',
          padding: isCompact ? '1.25vh 2vh' : '1vh 2vh'
        }}>
          <div id="cajaContenidoTitulo" style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            gap: '0'
          }}>
            <div id="cajaRetorno" style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}>
              <img
                src={obtenerRutaImagen(imagen_titulo)}
                alt=""
                style={{
                  width: '7.2vh',
                  height: '7.3vh',
                  objectFit: 'contain',
                  marginLeft: 0,
                  marginTop: 0,
                  flexShrink: 0,
                }}
              />
              <div id="cajaTextos" style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                marginLeft: isCompact ? '2vh' : '3.5vh',
                width: isCompact ? 'calc(100% - 10vh)' : 'auto'
              }}>
                <div id="cajaTexto" style={{
                  width: isCompact ? '100%' : '50.4vh',
                  height: isCompact ? 'auto' : 'auto',
                  transform: 'rotate(0deg)',
                  opacity: 1,
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: 'Inter',
                  fontWeight: '700',
                  fontStyle: 'normal',
                  fontSize: isCompact ? '2vh' : '2.5vh',
                  lineHeight: isCompact ? '1.15' : '1.15',
                  letterSpacing: '0%',
                  color: '#000',
                  marginTop: isCompact ? '0.5vh' : '0',
                  marginBottom: isCompact ? '0.35vh' : '0.25vh'
                }}>
                  {titulo}
                </div>
                {subtitulo && (
                  <div id="cajaSubtitulo" style={{
                    width: isCompact ? '100%' : '63.4vh',
                    height: isCompact ? 'auto' : 'auto',
                    transform: 'rotate(0deg)',
                    opacity: 1,
                    display: 'flex',
                    alignItems: isCompact ? 'flex-start' : 'flex-start',
                    fontFamily: 'Inter',
                    fontWeight: '400',
                    fontStyle: 'normal',
                    fontSize: isCompact ? '1.65vh' : '1.7vh',
                    lineHeight: isCompact ? '1.25' : '1.3',
                    letterSpacing: '0%',
                    color: '#858585',
                    paddingBottom: isCompact ? '1vh' : '0'
                  }}>
                    {subtitulo}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Titulo;
